import {
  ButtonHTMLAttributes,
  ElementType,
  MouseEvent,
  ReactNode,
  forwardRef,
} from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { ChevronDown } from "lucide-react";
import { BetaBadge } from "@punchup/ui-common/components/common/BetaBadge";
import { PUNCHUP_ICON_SIZE } from "../../../../icons";
import { cx } from "../../../../utils/cx";
import styles from "./PunchupSidebarNavItem.module.css";

export interface PunchupSidebarNavItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The row's visible text. Truncates with an ellipsis rather than wrapping. */
  label: string;
  /**
   * Depth in the nav tree. `parent` is a top-level row: leading icon, optional
   * disclosure caret. `child` is a nested row: no icon, indented so its text
   * lines up under the parent's label. Any `icon` passed to a `child` is ignored
   * — the indent, not a glyph, is what marks the nesting.
   */
  level?: "parent" | "child";
  /**
   * Leading glyph for a `parent` row — pass a `lucide-react` icon element. The
   * slot pins it to 24×24 and paints it via `currentColor`, so you may omit the
   * icon's own `size`/`color`; the row's state drives the colour.
   */
  icon?: ReactNode;
  /**
   * Marks this row as the user's current location — the orange tint + orange
   * text. Set it on the ONE row that matches the current page.
   */
  selected?: boolean;
  /**
   * Short qualifier rendered after the label as a `BetaBadge` (e.g. `"Beta"`,
   * `"New"`). Keep it to a word — the label truncates before the badge does.
   */
  badge?: string;
  /**
   * Trailing glyph after the label — pass a `lucide-react` element, e.g. an
   * external-link mark on a row that leaves the product. Like the caret it is
   * furniture: it holds one grey across every enabled state, so it never competes
   * with the label on a selected row. A row that expands shows the caret instead
   * — `expanded` wins when both are given.
   */
  trailingIcon?: ReactNode;
  /**
   * Presence of this prop renders the disclosure caret; its value is the open
   * state of the group this row heads (the chevron flips to point up when
   * `true`). Omit it entirely for a leaf row that navigates instead of expanding.
   */
  expanded?: boolean;
  /**
   * Render the single child element instead of a `<button>`, keeping every nav
   * style. Pass a `next/link` `<Link>` (or `<a>`) as the sole child so a row that
   * navigates stays a real link — middle-click, open-in-new-tab, context menu, a
   * crawlable `href`. The icon / badge / caret compose *inside* the slotted
   * element. Keep the `<button>` for a row that only expands a group.
   *
   * Combined with `disabled` the caller's element is dropped entirely — see the
   * `disabled` handling in the component body for why a disabled link cannot stay
   * an `<a>`.
   */
  asChild?: boolean;
  /**
   * The element to slot when `asChild` is set — pass the bare `<Link href=… />`
   * with no children of its own; this component supplies the row's contents. Has
   * no role on the default `<button>` path; the row's text is always `label`.
   */
  children?: ReactNode;
}

// forwardRef so a nav row can act as a Radix `asChild` trigger (a popover / tooltip
// on a collapsed rail clones the child and attaches a ref to the DOM node). The ref
// also reaches the slotted element when `asChild`.
export const PunchupSidebarNavItem = forwardRef<
  HTMLButtonElement,
  PunchupSidebarNavItemProps
>(
  (
    {
      label,
      level = "parent",
      icon,
      selected = false,
      badge,
      trailingIcon,
      expanded,
      asChild = false,
      disabled = false,
      className = "",
      children,
      ...props
    },
    ref
  ) => {
    // `asChild` renders the caller's element (e.g. a Link) via Radix Slot instead of
    // a <button>. `Slottable` marks that element as the merge target, so the icon,
    // label and caret compose *inside* it — without Slottable, Slot rejects a host
    // with more than one child. They MUST be DIRECT children of Comp: wrapping them
    // in a Fragment hides the Slottable from Slot and the merge silently no-ops.
    //
    // A DISABLED `asChild` row drops the caller's element and renders an inert
    // <span role="link"> instead, because a disabled link cannot stay an <a>:
    //   - `aria-disabled` is advisory ONLY. An <a href> still navigates on click and
    //     on Enter, so a screen-reader user is told "unavailable" while everyone
    //     else — including that same user — can still activate the row.
    //   - Guarding with a Slot-level onClick does NOT work. Radix composes the
    //     CHILD's handler first and the slot's second (react-slot mergeProps), so
    //     next/link has already called router.push before our preventDefault runs.
    //   - The <a> cannot be neutered in place either: Slot lets child props win, so
    //     `href` can't be stripped from here.
    // Dropping the anchor is also what a disabled link IS semantically — no
    // destination, not focusable, not in the tab order. `role="link"` keeps the row
    // announced as a link so the disabled state reads against the right noun.
    const isInertLink = asChild && disabled;
    const Comp: ElementType = isInertLink ? "span" : asChild ? Slot : "button";
    const isChild = level === "child";
    // A disabled row fires no handler on EITHER path. The <button> path gets this
    // free from the native attribute; the inert <span> has no such attribute, so the
    // guard is explicit — and it sits after the `{...props}` spread below so that a
    // caller's own onClick cannot outrank being disabled.
    const blockActivation = (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : "button"}
        className={cx(
          styles.navItem,
          isChild && styles.child,
          selected && styles.selected,
          className
        )}
        data-level={level}
        data-selected={selected ? "true" : "false"}
        // `disabled` is a <button> attribute and is not valid markup on the inert
        // <span>, so the signal always ALSO travels as `aria-disabled` — which is the
        // CSS hook too, so both element paths dim identically.
        disabled={asChild ? undefined : disabled}
        aria-disabled={disabled || undefined}
        role={isInertLink ? "link" : undefined}
        aria-current={selected ? "page" : undefined}
        aria-expanded={expanded}
        {...props}
        onClick={disabled ? blockActivation : props.onClick}
      >
        {!isChild && icon && (
          <span className={styles.iconSlot} aria-hidden>
            {icon}
          </span>
        )}
        {/* Skipped on the inert path: Slottable outside a Slot renders a plain
            Fragment, which would put the caller's live <a> back inside the span. */}
        {!isInertLink && <Slottable>{children}</Slottable>}
        <span className={styles.labelSlot}>
          <span className={styles.label}>{label}</span>
          {badge && <BetaBadge label={badge} />}
        </span>
        {expanded !== undefined ? (
          <ChevronDown
            className={cx(styles.caret, expanded && styles.caretExpanded)}
            size={PUNCHUP_ICON_SIZE.md}
            aria-hidden
          />
        ) : (
          trailingIcon && (
            <span className={styles.trailingIcon} aria-hidden>
              {trailingIcon}
            </span>
          )
        )}
      </Comp>
    );
  }
);

PunchupSidebarNavItem.displayName = "PunchupSidebarNavItem";
